import { DetailViewCard } from '../CardWithTitle';
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Comment,
  getCommentLoadFailedSubject,
  getCommentLoadingSubject,
  getCommentSubject,
  useCommentsStore
} from '../../../store/Comment';
import { ComponentSpinner } from '../Loading/ComponentSpinner';
import { DetailsCommentsCTA } from '../DetailsCommentsCTA/DetailsCommentsCTA';
import { CommentItem } from './CommentItem';
import { FocusedCommentScroll } from './FocusedCommentScroll';
import { useCommentChannel } from '../../../Api/sockets/commentChannel';
import { TextEditor } from './TextEditor';
import { mentionOf } from './mentionMarkup';
import { MentionableUser } from '../../../types';
import { getMentionableUsers } from '../../../store/MentionableUsers';
import { useSearchParams } from 'react-router-dom';
import { NotificationFocus } from '../Notifications/NotificationItem';
import { getAccountIdBySfId } from '../../../store/Accounts';
import { getFeatureFlagSubject } from '../../../Api/useFeatureFlags';
import { canAddComments } from '../../../store/User';

export interface DetailsCommentsProps {
  id: string;
  entityType: string;
  entityAccountId: string;
  className?: string;
}

export function DetailsComments({ id, entityType, entityAccountId, className }: DetailsCommentsProps) {
  const [search, setSearch] = useSearchParams();
  const focus = search.get('focus');
  const focusedCommentId = search.get('commentId');

  const [isFetching, setIsFetching] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState<boolean>(false);
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const [isReplying, setIsReplying] = useState<boolean>(false);
  const [sending, setSending] = useState<boolean>(false);
  const [collapsed, setCollapsed] = useState<boolean>(focus !== NotificationFocus.COMMENT);
  const [commentToReply, setCommentToReply] = useState<Comment | null>(null);
  const [draft, setDraft] = useState<string>('');
  const [replyDraft, setReplyDraft] = useState<string | null>(null);
  const [replySending, setReplySending] = useState<boolean>(false);
  const [storeThread, setStoreThread] = useState<Comment[]>([]);
  const [mentionableUsers, setMentionableUsers] = useState<MentionableUser[]>([]);
  const recordEpoch = useRef(0);
  const { fetchComments, createComment } = useCommentsStore();
  // In the render where the record changes, the store still holds the previous record's thread.
  const comments = useMemo(
    () => storeThread.filter((c) => c.entity_id === id && c.entity_type === entityType),
    [storeThread, id, entityType]
  );

  useCommentChannel(id, entityType);

  useEffect(() => {
    let requestId = 0;
    let lastEnabled: boolean | null = null;

    const loadMentionableUsers = async () => {
      const activeRequest = ++requestId;
      const accountId = await getAccountIdBySfId(entityAccountId);
      const users = await getMentionableUsers(accountId);
      if (activeRequest === requestId) setMentionableUsers(users);
    };

    const commentSub = getCommentSubject().subscribe(setStoreThread);
    const commentLoadingSub = getCommentLoadingSubject().subscribe((loading) => setIsFetching(loading));
    const loadFailedSub = getCommentLoadFailedSubject().subscribe(setLoadFailed);
    const flagSub = getFeatureFlagSubject().subscribe(({ mentionable_upstackers = false }) => {
      if (mentionable_upstackers === lastEnabled) return;
      lastEnabled = mentionable_upstackers;
      loadMentionableUsers();
    });

    return () => {
      requestId += 1;
      commentSub.unsubscribe();
      commentLoadingSub.unsubscribe();
      loadFailedSub.unsubscribe();
      flagSub.unsubscribe();
    };
  }, [entityAccountId]);

  // A later notification can change only the comment, when an earlier lookup that failed left its focus in the URL.
  useEffect(() => {
    if (focus === NotificationFocus.COMMENT) setCollapsed(false);
  }, [focus, focusedCommentId]);

  const clearFocus = () => {
    search.delete('focus');
    search.delete('commentId');
    setSearch(search, { replace: true });
  };

  useEffect(() => {
    if (id) fetchComments(id, entityType);
  }, [id, entityType]);

  // The panel can stay mounted while its record changes; nothing typed or sent for one record may act on the next.
  useEffect(() => {
    recordEpoch.current += 1;
    setIsFormOpen(false);
    setIsReplying(false);
    setCommentToReply(null);
    setDraft('');
    setReplyDraft(null);
    setSending(false);
    setReplySending(false);
  }, [id, entityType]);

  const canComment = canAddComments();

  const cardActions = () => {
    if (!canComment) return undefined;

    return (
      <DetailsCommentsCTA
        setIsFormOpen={() => {
          setIsFormOpen(true);
          setCollapsed(false);
        }}
      />
    );
  };

  const handleSend = async (commentText: string) => {
    const epoch = recordEpoch.current;
    setDraft(commentText);
    setSending(true);
    const saved = await createComment({ id: '', entity_id: id, entity_type: entityType, comment_text: commentText });
    if (epoch !== recordEpoch.current) return;
    setSending(false);
    if (!saved) return;
    setDraft('');
    setIsFormOpen(false);
  };

  const handleCancel = () => {
    setDraft('');
    setIsFormOpen(false);
  };

  const sortComments = (comments: Comment[]) => {
    return comments.sort((a, b) => {
      return a?.created_at && b?.created_at ? new Date(b.created_at).getTime() - new Date(a.created_at).getTime() : 1;
    });
  };

  const commentTextWithMention = () => {
    const name = commentToReply?.portal_user_name;
    const email = commentToReply?.portal_user_email;
    // The mention is keyed by the author's email, and a deleted comment masks both it and the name.
    return commentToReply?.parent_comment_id && name && email ? mentionOf(name, email) : '';
  };

  const renderComments = () => {
    return sortComments(comments.filter((c) => !c.parent_comment_id)).map((comment) => (
      <Fragment key={comment.id}>
        <CommentItem
          comment={comment}
          handleReply={handleReply}
          mentionableUsers={mentionableUsers}
          canReply={canComment}
        />
        {sortComments(comments.filter((r) => String(r.parent_comment_id) == comment.id))
          .reverse()
          .map((reply) => (
            <CommentItem
              comment={reply}
              handleReply={handleReply}
              key={reply.id}
              mentionableUsers={mentionableUsers}
              canReply={canComment}
            />
          ))}
        {isReplying &&
          !replySending &&
          (commentToReply == comment || String(commentToReply?.parent_comment_id) == comment.id) && (
            <div className="mt-4 ml-6">
              <TextEditor
                commentText={replyDraft ?? commentTextWithMention()}
                handleCancel={handleCancelReply}
                handleSend={handleCreateReply}
                portalUsers={mentionableUsers}
                placeholder="Reply to this comment..."
              />
            </div>
          )}
      </Fragment>
    ));
  };

  const handleReply = (comment: Comment) => {
    setCommentToReply(comment);
    setReplyDraft(null);
    setIsReplying(true);
  };

  const handleCancelReply = () => {
    setCommentToReply(null);
    setReplyDraft(null);
    setIsReplying(false);
  };

  const handleCreateReply = async (comment_text: string) => {
    if (!commentToReply) return;
    const epoch = recordEpoch.current;
    setReplyDraft(comment_text);
    setReplySending(true);
    const saved = await createComment({
      id: '',
      entity_id: commentToReply.entity_id,
      entity_type: commentToReply.entity_type,
      comment_text: comment_text,
      parent_comment_id: Number(commentToReply.parent_comment_id || commentToReply.id)
    });
    if (epoch !== recordEpoch.current) return;
    setReplySending(false);
    if (saved) handleCancelReply();
  };

  return (
    <DetailViewCard
      cardActions={cardActions()}
      collapsed={collapsed}
      onCollapseClick={setCollapsed}
      enableCollapse
      className={className}
      title={
        isFetching || loadFailed
          ? 'Comments'
          : `Comments (${comments.filter((c) => !c.parent_comment_id)?.length || 0})`
      }
      childrenWrapperClass="overflow-x-auto">
      <div className="font-sans text-black">
        {(isFetching || sending) && <ComponentSpinner label={sending ? 'Processing' : 'Loading'} />}
        {!isFetching && !sending && loadFailed && <div className="text-center">Comments could not be loaded</div>}
        {!isFetching && !sending && isFormOpen && (
          <TextEditor
            commentText={draft}
            handleCancel={handleCancel}
            handleSend={handleSend}
            portalUsers={mentionableUsers}
            placeholder="Comment here"
          />
        )}
        {!isFetching && !sending && !loadFailed && !comments?.length && !isFormOpen && (
          <div className="text-center">No comments</div>
        )}
        {!isFetching && !sending && !!comments?.length && renderComments()}
        {!isFetching && !sending && focus === NotificationFocus.COMMENT && !!comments?.length && (
          <FocusedCommentScroll
            commentId={focusedCommentId || ''}
            thread={comments}
            onScrolled={clearFocus}
          />
        )}
      </div>
    </DetailViewCard>
  );
}
